import { useCallback, useEffect, useState } from "react";
import {
  CalendarBlank, CheckCircle, Clock, FilePdf, Plus, Timer, Warning,
} from "@phosphor-icons/react";
import { api, errMsg, fileUrl } from "@/lib/api";
import { EmptyState, StateBadge } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August",
  "September", "October", "November", "December"].map((m) => `${m} 2026`);
const WEEKS = ["Week 1", "Week 2", "Week 3", "Week 4", "Week 5"];

const Tile = ({ label, value, icon: Icon, tone, testid }) => (
  <div className={`rounded-2xl border px-5 py-4 ${tone}`} data-testid={testid}>
    <p className="font-mono text-3xl tabular-nums">{value}</p>
    <p className="mt-1 flex items-center gap-1.5 text-[10px] uppercase tracking-[0.14em]">
      {Icon && <Icon size={12} />} {label}
    </p>
  </div>
);

export const BmWeeklyReview = ({ base, can, title = "weekly review", elementName = "" }) => {
  const [month, setMonth] = useState(MONTHS[7]);
  const [data, setData] = useState(null);
  const [active, setActive] = useState(null);
  const [form, setForm] = useState({ week: WEEKS[0], coordinator: "", planned_date: "", planned_time: "3 PM - 4 PM" });

  const load = useCallback(async () => {
    try {
      const { data: res } = await api.get(`${base}/weekly-reviews`, { params: { month } });
      setData(res);
      setActive((prev) => (prev ? res.rows.find((r) => r.id === prev.id) || null : null));
    } catch (e) { setData({ error: errMsg(e) }); }
  }, [base, month]);

  useEffect(() => { load(); }, [load]);

  const create = async () => {
    try {
      const { data: row } = await api.post(`${base}/weekly-reviews`, { ...form, month });
      setActive(row);
      toast.success(`${row.week} review created`);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const saveActive = async (patch) => {
    try {
      const { data: row } = await api.put(`${base}/weekly-reviews/${active.id}`, patch);
      setActive(row);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const submitActive = async () => {
    try {
      await api.post(`${base}/weekly-reviews/${active.id}/submit`);
      toast.success("Weekly review submitted");
      setActive(null);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  if (!data) return <p className="label-caps animate-pulse" data-testid="bmwr-loading">Loading weekly review</p>;
  if (data.error) return <EmptyState testid="bmwr-error" title="Weekly review unavailable" message={data.error} />;

  const setChecklist = (index, done) => {
    const checklist = active.checklist.map((c, i) => (i === index ? { ...c, done } : c));
    saveActive({ checklist });
  };

  return (
    <div className="space-y-6" data-testid="bm-weekly-review">
      <section className="panel overflow-hidden">
        <div className="relative overflow-hidden bg-navy px-6 py-8 text-white md:px-10">
          <div className="absolute -right-10 -top-16 h-56 w-56 rounded-full bg-gold/20 blur-3xl" />
          <p className="label-caps text-white/60">{elementName}</p>
          <h1 className="mt-2 font-display text-3xl capitalize md:text-4xl">{title}</h1>
          <p className="mt-2 max-w-xl text-sm text-white/70">
            Plan the week, run the review flow with the coordinator and freeze it as a signed record.
          </p>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border px-6 py-4">
          <p className="label-caps">Weekly reviews</p>
          <div className="flex items-center gap-2">
            <CalendarBlank size={14} className="text-muted-foreground" />
            <Select value={month} onValueChange={setMonth}>
              <SelectTrigger data-testid="bmwr-month" className="h-9 w-44 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>{MONTHS.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4 p-6 lg:grid-cols-5">
          <Tile testid="bmwr-stat-total" label="Total weeks" value={data.stats.total}
                tone="border-deepblue/25 bg-deepblue/8 text-navy" />
          <Tile testid="bmwr-stat-completed" label="Completed" value={data.stats.completed} icon={CheckCircle}
                tone="border-success/30 bg-success/10 text-success" />
          <Tile testid="bmwr-stat-pending" label="Pending" value={data.stats.pending} icon={Timer}
                tone="border-gold/40 bg-gold/12 text-navy" />
          <Tile testid="bmwr-stat-ontime" label="On time" value={data.stats.on_time} icon={Clock}
                tone="border-success/25 bg-sky/15 text-navy" />
          <Tile testid="bmwr-stat-delayed" label="Delayed" value={data.stats.delayed} icon={Warning}
                tone="border-destructive/30 bg-destructive/8 text-destructive" />
        </div>

        <div className="hidden grid-cols-[3rem_1.2fr_1fr_1fr_1fr_1fr_0.9fr_0.8fr_0.9fr_6rem] gap-3 border-y border-border px-6 py-3 text-[9px] uppercase tracking-[0.12em] text-muted-foreground md:grid">
          <span>Sl</span><span>Coordinator</span><span>Week</span><span>Planned date</span>
          <span>Planned time</span><span>Actual date</span><span>Status</span><span>On time</span>
          <span>Reminder</span><span>Action</span>
        </div>
        <div className="divide-y divide-border">
          {data.rows.map((r, i) => (
            <div key={r.id} data-testid={`bmwr-row-${r.id}`}
                 className="grid grid-cols-1 gap-2 px-6 py-4 transition-colors hover:bg-sky/10 md:grid-cols-[3rem_1.2fr_1fr_1fr_1fr_1fr_0.9fr_0.8fr_0.9fr_6rem] md:items-center md:gap-3">
              <span className="font-mono text-xs text-muted-foreground">{i + 1}</span>
              <span className="text-sm text-navy">{r.coordinator || "—"}</span>
              <span className="text-xs text-foreground/70">{r.week}</span>
              <span className="font-mono text-xs text-muted-foreground">{r.planned_date || "—"}</span>
              <span className="text-xs text-foreground/70">{r.planned_time || "—"}</span>
              <span className="font-mono text-xs text-muted-foreground">{r.actual_date || "—"}</span>
              <StateBadge state={r.status} />
              <span className={`text-[10px] uppercase tracking-[0.14em] ${
                r.on_time === true ? "text-success" : r.on_time === false ? "text-destructive" : "text-muted-foreground"}`}>
                {r.on_time === true ? "Yes" : r.on_time === false ? "No" : "—"}
              </span>
              <input type="checkbox" data-testid={`bmwr-reminder-${r.id}`} checked={!!r.reminder_mail}
                     disabled={!can("edit")}
                     onChange={async (e) => {
                       await api.put(`${base}/weekly-reviews/${r.id}`, { reminder_mail: e.target.checked })
                         .catch((err) => toast.error(errMsg(err)));
                       load();
                     }}
                     className="h-4 w-4 accent-navy" />
              <div className="flex items-center gap-2">
                <button data-testid={`bmwr-open-${r.id}`} onClick={() => setActive(r)}
                        className="text-[10px] uppercase tracking-[0.14em] text-deepblue hover:underline">
                  Open
                </button>
                <a data-testid={`bmwr-pdf-${r.id}`} href={fileUrl(`${base}/weekly-reviews/${r.id}/pdf`)}
                   className="text-gold hover:text-navy"><FilePdf size={16} /></a>
              </div>
            </div>
          ))}
          {data.rows.length === 0 && (
            <EmptyState testid="bmwr-empty" title="No weekly reviews for this month"
                        message="Start a review below to plan the week with your coordinator." />
          )}
        </div>
      </section>

      {can("create") && (
        <section className="panel p-6 md:p-8" data-testid="bmwr-start">
          <p className="label-caps">Start review</p>
          <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-[1fr_1.2fr_1fr_1fr_auto] md:items-end">
            <div className="space-y-2">
              <Label className="label-caps">Week</Label>
              <Select value={form.week} onValueChange={(v) => setForm({ ...form, week: v })}>
                <SelectTrigger data-testid="bmwr-new-week"><SelectValue /></SelectTrigger>
                <SelectContent>{WEEKS.map((w) => <SelectItem key={w} value={w}>{w}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {[["Coordinator", "coordinator", "text"], ["Planned date", "planned_date", "date"],
              ["Planned time", "planned_time", "text"]].map(([label, key, type]) => (
              <div key={key} className="space-y-2">
                <Label className="label-caps">{label}</Label>
                <Input type={type} data-testid={`bmwr-new-${key}`} value={form[key]}
                       onChange={(e) => setForm({ ...form, [key]: e.target.value })} />
              </div>
            ))}
            <Button data-testid="bmwr-create" onClick={create}
                    className="h-10 rounded-full text-[10px] uppercase tracking-[0.16em]">
              <Plus size={13} className="mr-1.5" /> Start review
            </Button>
          </div>
        </section>
      )}

      {active && (
        <section className="panel overflow-hidden" data-testid="bmwr-flow">
          <div className="flex flex-wrap items-center justify-between gap-3 bg-gradient-to-r from-navy via-navy to-deepblue px-6 py-5 text-white">
            <div>
              <p className="label-caps text-white/60">Weekly review flow</p>
              <p className="mt-1 font-display text-2xl">{active.week} · {active.month}</p>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" data-testid="bmwr-select-all"
                      className="h-9 rounded-full border-white/40 bg-transparent text-[10px] uppercase tracking-[0.16em] text-white hover:bg-card/10"
                      onClick={() => saveActive({ checklist: active.checklist.map((c) => ({ ...c, done: true })) })}>
                Select all
              </Button>
              <Button variant="outline" data-testid="bmwr-clear-all"
                      className="h-9 rounded-full border-white/40 bg-transparent text-[10px] uppercase tracking-[0.16em] text-white hover:bg-card/10"
                      onClick={() => saveActive({ checklist: active.checklist.map((c) => ({ ...c, done: false })) })}>
                Clear all
              </Button>
              <Button data-testid="bmwr-add-row"
                      className="h-9 rounded-full bg-gold text-[10px] uppercase tracking-[0.16em] text-navy hover:bg-gold/85"
                      onClick={() => saveActive({
                        checklist: [...active.checklist, { text: "New review step", done: false }] })}>
                <Plus size={13} className="mr-1.5" /> Add row
              </Button>
            </div>
          </div>

          <div className="divide-y divide-border">
            {(active.checklist || []).map((item, index) => (
              <label key={index} data-testid={`bmwr-check-${index}`}
                     className="flex cursor-pointer items-center justify-between gap-4 px-6 py-4 transition-colors hover:bg-sky/10">
                <span className="text-sm text-navy">{item.text}</span>
                <input type="checkbox" checked={!!item.done} className="h-4 w-4 shrink-0 accent-navy"
                       onChange={(e) => setChecklist(index, e.target.checked)} />
              </label>
            ))}
          </div>

          <div className="space-y-4 border-t border-border p-6">
            <div className="space-y-2">
              <Label className="label-caps">Observations</Label>
              <Textarea data-testid="bmwr-notes" defaultValue={active.notes || ""}
                        onBlur={(e) => saveActive({ notes: e.target.value })} />
            </div>
            <div className="flex flex-wrap justify-end gap-3">
              <Button asChild variant="outline" className="h-10 rounded-full text-[10px] uppercase tracking-[0.16em]">
                <a data-testid="bmwr-download-pdf" href={fileUrl(`${base}/weekly-reviews/${active.id}/pdf`)}>
                  <FilePdf size={14} className="mr-1.5" /> Download PDF
                </a>
              </Button>
              <Button data-testid="bmwr-submit" onClick={submitActive}
                      className="h-10 rounded-full text-[10px] uppercase tracking-[0.16em]">
                Submit review
              </Button>
            </div>
          </div>
        </section>
      )}
    </div>
  );
};
